// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch, setRecruiter } from '../../../store';
import styles from './interviewHost.module.scss';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { fetchInterviewHostDashboard } from '../../../store/interviewHostSlice';
import PageLoader from '../../../components/PageLoader';
import { Sidebar } from '../../../components/Sidebar';

interface ActionCard {
  id: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  action: () => void;
}

const InterviewHostAgent: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useSelector((state: RootState) => state.auth.user);
  const { data, loading, error } = useSelector((state: RootState) => state.interviewHost);

  // Fetch dashboard data on mount and set user in Redux
  useEffect(() => {
    if (!data) {
      dispatch(fetchInterviewHostDashboard());
    } else if (data.recruiter && !user) {
      // Set user in Redux auth state when dashboard data is loaded
      dispatch(
        setRecruiter({
          name: data.recruiter.name,
          username: data.recruiter.email
        })
      );
    }
  }, [data, dispatch, user]);

  // Format last login time
  const formatLastLogin = (lastLoginDate: string): string => {
    const loginDate = new Date(lastLoginDate);
    const today = new Date();
    const isToday = loginDate.toDateString() === today.toDateString();

    const timeString = loginDate.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });

    return isToday ? `today at ${timeString}` : loginDate.toLocaleDateString();
  };

  // Define logout handler first
  const handleLogout = async (): Promise<void> => {
    try {
      window.location.href = '/api/sso/logout';
    } catch (err) {
      console.error('Logout failed:', err);
    }
  };

  // Show loading state
  if (loading) {
    return <PageLoader label="Loading dashboard..." activeNav="home" showSidebar={false} />;
  }

  // Show error state
  if (error || !data) {
    return (
      <div className={styles.container}>
        <Sidebar activeNav="home" user={user} onLogout={handleLogout} />
        <main className={styles.mainContent}>
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <h2>Failed to load dashboard</h2>
            <p>{error || 'An unexpected error occurred.'}</p>
            <button onClick={() => dispatch(fetchInterviewHostDashboard())} style={{ marginTop: '1rem' }}>
              Try Again
            </button>
          </div>
        </main>
      </div>
    );
  }

  const { recruiter, welcome } = data;
  const userName = recruiter?.name || user?.name || 'Guest';
  const firstName = userName.split(' ')[0];
  const lastLogin = recruiter ? formatLastLogin(recruiter.last_login) : 'today';
  const pendingCandidates = welcome?.awaiting_decision || 0;

  const actionCards: ActionCard[] = [
    {
      id: 'view-jobs',
      icon: <WorkOutlineIcon sx={{ fontSize: 24 }} />,
      title: 'View Job Roles',
      description: 'See all open requisitions, candidate pipelines, and where each role stands.',
      action: () => {
        navigate('/jobs');
      }
    }
  ];

  return (
    <div className={styles.container}>
      <Sidebar activeNav="home" user={user || { name: userName }} onLogout={handleLogout} />
      <main className={styles.mainContent}>
        <div className={styles.header}>
          <div className={styles.logoContainer}>
            <h1 className={styles.title}>Interviewer Host</h1>
          </div>
        </div>

        <div className={styles.welcomeSection}>
          <h2 className={styles.welcomeTitle}>
            Hi {firstName}, welcome back <span className={styles.waveEmoji}>👋</span>
          </h2>
          <p className={styles.subtitle}>
            Your AI-powered interview platform — manage jobs, candidates, and interview reports all in one place.
          </p>
        </div>

        <div className={styles.actionsSection}>
          <h3 className={styles.sectionTitle}>How would you like to start?</h3>
          <div className={styles.cardsGrid}>
            {actionCards.map((card) => (
              <button key={card.id} className={styles.actionCard} onClick={card.action}>
                <div className={styles.cardIcon}>{card.icon}</div>
                <div className={styles.cardContent}>
                  <h4 className={styles.cardTitle}>{card.title}</h4>
                  <p className={styles.cardDescription}>{card.description}</p>
                  <div className={styles.cardArrow}>
                    <ArrowForwardIcon sx={{ fontSize: 20 }} />
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className={styles.footer}>
          <p className={styles.footerText}>
            Last login: {lastLogin} · {pendingCandidates} candidates awaiting your decision
          </p>
        </div>
      </main>
    </div>
  );
};

export default InterviewHostAgent;
